import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import CustomTable from 'common/components/CustomTable'
import 'dayjs/locale/es'
import { useEffect, useState } from 'react'
import useFetchPedidosPorRango from '../../hooks/useFetchPedidosPorRango'

const ModalRangoPedidos = ({ open, onClose, idPlanificacion, onSuccess }) => {
  const theme = useTheme()
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'))

  // Estados locales
  const [fechaDesde, setFechaDesde] = useState(null)
  const [fechaHasta, setFechaHasta] = useState(null)
  const [mensaje, setMensaje] = useState(null)
  const [busquedaRealizada, setBusquedaRealizada] = useState(false)

  // Estado para Dialog (confirmaciones)
  const [dialog, setDialog] = useState({
    open: false,
    title: '',
    message: '',
    onConfirm: null,
  })

  // Hook para buscar pedidos por rango (solo visualización)
  const {
    response: pedidos,
    loading: loadingPedidos,
    error: errorBusqueda,
    fetchPedidos,
    reset: resetPedidos,
  } = useFetchPedidosPorRango({ idPlanificacion })

  // Limpiar campos al cerrar
  useEffect(() => {
    if (!open) {
      setFechaDesde(null)
      setFechaHasta(null)
      setMensaje(null)
      setBusquedaRealizada(false)
      resetPedidos()
    }
  }, [open, resetPedidos])

  // Mostrar error de búsqueda
  useEffect(() => {
    if (errorBusqueda) {
      setMensaje({
        tipo: 'error',
        texto: 'Error al buscar pedidos: ' + errorBusqueda,
      })
    }
  }, [errorBusqueda])

  // Mostrar mensaje si no hay resultados (solo después de buscar)
  useEffect(() => {
    if (busquedaRealizada && !loadingPedidos && pedidos.length === 0) {
      setMensaje({
        tipo: 'info',
        texto: 'No se encontraron pedidos en el rango de fechas especificado',
      })
    }
  }, [pedidos, loadingPedidos, busquedaRealizada])

  // Funciones para manejar el Dialog
  const mostrarConfirmacion = (title, message, onConfirm) => {
    setDialog({ open: true, title, message, onConfirm })
  }

  const cerrarDialog = () => {
    setDialog({ open: false, title: '', message: '', onConfirm: null })
  }

  const confirmarDialog = () => {
    dialog.onConfirm && dialog.onConfirm()
    cerrarDialog()
  }

  // Contadores para los botones
  const pedidosParaAgregar = pedidos.filter((p) => !p.id_planificacion_pedido)
  const pedidosParaBorrar = pedidos.filter((p) => p.id_planificacion_pedido)

  // Función para buscar pedidos por rango de fechas (solo visualización)
  const handleBuscar = () => {
    if (!fechaDesde && !fechaHasta) {
      setMensaje({
        tipo: 'error',
        texto: 'Debe seleccionar al menos una fecha',
      })
      return
    }

    setMensaje(null)
    setBusquedaRealizada(true)

    // Llamar al hook para buscar pedidos (solo para mostrarlos en la tabla)
    const desde = fechaDesde ? fechaDesde.format('YYYY-MM-DD') : null
    const hasta = fechaHasta ? fechaHasta.format('YYYY-MM-DD') : null

    fetchPedidos(desde, hasta)
  }

  // Handlers
  const handleCancelar = () => {
    onClose()
  }

  const handleAgregar = () => {
    if (!fechaDesde && !fechaHasta) {
      setMensaje({
        tipo: 'error',
        texto: 'Debe seleccionar al menos una fecha',
      })
      return
    }
    const pedidosParaAgregar = pedidos.filter((p) => !p.id_planificacion_pedido)

    if (pedidosParaAgregar.length === 0) {
      setMensaje({
        tipo: 'warning',
        texto: 'Todos los pedidos del rango ya están añadidos a la planificación',
      })
      return
    }

    mostrarConfirmacion(
      'Añadir pedidos',
      `¿Está seguro de que desea añadir ${pedidosParaAgregar.length} pedido(s) a la planificación?`,
      () => {
        // Pasar los pedidos al componente padre para que actualice su estado local
        if (onSuccess) {
          onSuccess('A', pedidosParaAgregar)
        }
        // Cerrar el modal
        onClose()
      },
    )
  }

  const handleBorrar = () => {
    if (!fechaDesde && !fechaHasta) {
      setMensaje({
        tipo: 'error',
        texto: 'Debe seleccionar al menos una fecha',
      })
      return
    }

    const pedidosParaBorrar = pedidos.filter((p) => p.id_planificacion_pedido)

    if (pedidosParaBorrar.length === 0) {
      setMensaje({
        tipo: 'error',
        texto: 'Ninguno de los pedidos del rango está añadido a la planificación',
      })
      return
    }

    mostrarConfirmacion(
      'Borrar pedidos',
      `¿Está seguro de que desea borrar ${pedidosParaBorrar.length} pedido(s) de la planificación?`,
      () => {
        // Pasar los pedidos al componente padre para que actualice su estado local
        if (onSuccess) {
          onSuccess('B', pedidosParaBorrar)
        }
        // Cerrar el modal
        onClose()
      },
    )
  }

  // Columnas de la tabla (sin checkboxes, solo visualización)
  const columns = [
    {
      key: 'Nº Pedido',
      value: 'numero_pedido',
      width: 120,
    },
    {
      key: 'Cliente',
      value: 'cliente',
      width: 150,
    },
    {
      key: 'Artículo',
      value: 'articulo',
      flex: 1,
    },
    {
      key: 'Fecha Entrega',
      value: 'fecha_entrega',
      width: 150,
      renderCell: (row) => {
        if (!row.fecha_entrega) return ''
        return new Date(row.fecha_entrega).toLocaleDateString('es-ES')
      },
    },
    {
      key: 'Cantidad',
      value: 'cantidad',
      width: 120,
      renderCell: (row) => {
        if (!row.cantidad) return ''
        return parseFloat(row.cantidad).toFixed(2)
      },
    },
  ]

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="es">
      <Dialog open={open} onClose={onClose} fullScreen={fullScreen} maxWidth="lg" fullWidth>
        <DialogTitle>Añadir o borrar pedidos por rango de fechas</DialogTitle>

        <DialogContent>
          {mensaje && (
            <Alert severity={mensaje.tipo} sx={{ mb: 2 }}>
              {mensaje.texto}
            </Alert>
          )}

          {/* Filtros de fecha + Botón Buscar */}
          <Box sx={{ display: 'flex', gap: 2, mb: 3, alignItems: 'center' }}>
            <DatePicker
              label="Fecha desde"
              value={fechaDesde}
              onChange={(newValue) => setFechaDesde(newValue)}
              format="DD/MM/YYYY"
              slotProps={{
                textField: { size: 'small', fullWidth: true },
              }}
            />
            <DatePicker
              label="Fecha hasta"
              value={fechaHasta}
              onChange={(newValue) => setFechaHasta(newValue)}
              format="DD/MM/YYYY"
              slotProps={{
                textField: { size: 'small', fullWidth: true },
              }}
            />
            <Button
              variant="contained"
              onClick={handleBuscar}
              disabled={loadingPedidos}
              sx={{ minWidth: 120, height: 40 }}
            >
              {loadingPedidos ? <CircularProgress size={24} /> : 'Buscar'}
            </Button>
          </Box>

          {/* Sección de pedidos */}
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
              Pedidos en el rango seleccionado ({pedidos.length})
            </Typography>

            {pedidos.length > 0 ? (
              <CustomTable
                dataSource={pedidos}
                fields={columns}
                rowid="id"
                loading={loadingPedidos}
                initialPageSize={10}
                disableColumnFilter
                disableColumnSelector
                disableDensitySelector
              />
            ) : (
              <Box sx={{ p: 4, textAlign: 'center', bgcolor: 'grey.50', borderRadius: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  {loadingPedidos
                    ? 'Buscando pedidos...'
                    : 'Use los filtros de fecha y haga clic en "Buscar" para ver los pedidos'}
                </Typography>
              </Box>
            )}
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button onClick={handleCancelar} variant="outlined" disabled={loadingPedidos}>
            Cancelar
          </Button>

          <Box sx={{ flex: 1 }} />

          {loadingPedidos ? (
            <CircularProgress size={24} />
          ) : (
            <>
              <Button
                onClick={handleBorrar}
                variant="contained"
                color="error"
                disabled={pedidosParaBorrar.length === 0}
              >
                Borrar estos pedidos ({pedidosParaBorrar.length})
              </Button>
              <Button
                onClick={handleAgregar}
                variant="contained"
                color="primary"
                disabled={pedidosParaAgregar.length === 0}
              >
                Añadir estos pedidos ({pedidosParaAgregar.length})
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* Dialog para confirmaciones */}
      <Dialog
        open={dialog.open}
        onClose={cerrarDialog}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">{dialog.title}</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">{dialog.message}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={cerrarDialog} color="inherit">
            Cancelar
          </Button>
          <Button onClick={confirmarDialog} variant="contained" autoFocus>
            Confirmar
          </Button>
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  )
}

export default ModalRangoPedidos
